import { Box } from '@mui/material';
import type { ContractType } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

/**
 * Billing-type letter chips: F (fixed, maroon) / T (T&M, blue) / R (retainer, purple).
 *
 * Never pink (Mette, 3 Nov): pink is the brand's action colour. A badge is a
 * soft fill — the hue's 40 with its 100 for the letter — not the full 80;
 * `fg`/`bg` (80 on 10) stay for tinted text, and `line` (60) for outlines.
 *
 * `letter` and `label` are the English values, kept for existing callers; the
 * translated ones are `pmCommon:billingLetter.<type>` / `pmCommon:billingMethod.<type>`.
 */
export const CONTRACT_TYPE_STYLES: Record<
  ContractType,
  {
    letter: string;
    label: string;
    fg: string;
    bg: string;
    line: string;
    badgeBg: string;
    badgeFg: string;
  }
> = {
  fixed: {
    letter: 'F',
    label: 'Fixed price',
    fg: 'var(--color-maroon-80)',
    bg: 'var(--color-maroon-10)',
    line: 'var(--color-maroon-60)',
    badgeBg: 'var(--color-maroon-40)',
    badgeFg: 'var(--color-maroon-100)',
  },
  tm: {
    letter: 'T',
    label: 'T&M',
    fg: 'var(--color-blue-80)',
    bg: 'var(--color-blue-10)',
    line: 'var(--color-blue-60)',
    badgeBg: 'var(--color-blue-40)',
    badgeFg: 'var(--color-blue-100)',
  },
  retainer: {
    letter: 'R',
    label: 'Retainer',
    fg: 'var(--color-purple-80)',
    bg: 'var(--color-purple-10)',
    line: 'var(--color-purple-60)',
    badgeBg: 'var(--color-purple-40)',
    badgeFg: 'var(--color-purple-100)',
  },
};

export type ContractChipProps = {
  type: ContractType;
  /** Contract name (multi-contract projects) or the billing label. */
  label?: string;
  size?: number;
};

/** The square letter badge, optionally with a label beside it. */
export const ContractChip = ({ type, label, size = 17 }: ContractChipProps) => {
  const { t } = useTranslation('pmCommon');
  const style = CONTRACT_TYPE_STYLES[type];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="span"
        sx={{
          width: size,
          height: size,
          flex: 'none',
          borderRadius: '4px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 600,
          fontFamily: 'var(--font-family-mono)',
          bgcolor: style.badgeBg,
          color: style.badgeFg,
        }}
      >
        {t(`billingLetter.${type}`)}
      </Box>
      {label ? (
        <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-1)' }}>
          {label}
        </Box>
      ) : null}
    </Box>
  );
};
